import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator, ScrollView,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../constants/Colors';
import getInsightNotifications from '../requests/Notifications/GetInsightNotifications';
import NotificationItem from './NotificationsLog/NotificationItem';
import BuildingInsightsEmptyImage from './Home/BuildingInsightsEmptyImage';
import Style from '../constants/Style';
import { DateRange } from '../redux/dateRangeSlice';
import { Notification, getNotificationData } from './NotificationsLog/data';
import markNotificationsRead from '../requests/Notifications/MarkNotificationsRead';
import { updateBadgeCount } from '../redux/selectedBuildingSlice';
import { HappyScoreCategory } from '../constants/Types';

interface TopSectionInsightsProps {
  average: number,
  yesterdayScore: number,
  todayScore: number,
  screen: HappyScoreCategory,
  buttonShown?: boolean,
}

const TopSectionInsights = ({
  average, yesterdayScore, todayScore, screen, buttonShown,
}: TopSectionInsightsProps) => {
  const range: DateRange = useSelector(
    (state: { dateRange: DateRange }) => state.dateRange,
  );
  const percentageChange: number | null = Math.floor(
    100 * ((todayScore - yesterdayScore) / yesterdayScore),
  );
  const difference: string = Number.isFinite(percentageChange)
    ? `${percentageChange}%`
    : '-';
  const [notificationData, setNotificationData] = useState(null);
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [loading, setLoading] = useState<boolean>(false);
  const badgeCount: number = useSelector(
    (state: { selectedBuilding: { badgeCount: number } }) => state.selectedBuilding.badgeCount,
  );
  const dispatch = useDispatch();

  const loadInsights = (buildingLocationId: string) => {
    setLoading(true);
    getInsightNotifications(buildingLocationId, screen, range.start, range.end)
      .then((res) => setNotificationData(getNotificationData(res.Notifications)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!(buildingId?.length > 0)) return;
    loadInsights(buildingId);
  }, [buildingId, screen]);

  // eslint-disable-next-line no-unused-vars
  const expand: Function = (notification: Notification) => {
    if (!notification.isRead) {
      markNotificationsRead(notification.id, true);
      dispatch(updateBadgeCount(badgeCount - 1));
    }
    setNotificationData(notificationData.map((dataItem: Notification) => (dataItem.id
      === notification.id ? { ...notification, isRead: true, expanded: !notification.expanded }
      : dataItem)));
  };

  return <View
    style={styles.container}
    testID='notification-center-home-screen'
  >
    <View style={styles.scoreSection}>
      <Text style={styles.scoreValue}>{average ? Math.floor(average) : '-'}</Text>
      <Text style={styles.scoreTitle}>{'Average'}</Text>
      <View style={styles.scoreSectionItemSeparator} />
      <Text style={styles.scoreValue}>{difference}</Text>
      <Text style={styles.scoreTitle}>{'Difference from yesterday'}</Text>
    </View>
    <View style={styles.insightsSection}>
      <ScrollView style={[styles.list, loading && styles.loadingOpacity]}>
        {notificationData?.length > 0
          ? notificationData.map((item, index: number) => <NotificationItem
            key={`insight_notification_item_${index}`}
            {...{ ...item }}
            dropdownView
            buttonHidden={!buttonShown}
          />) : <View style={[styles.emptyState, loading && styles.loadingOpacity]}>
            <BuildingInsightsEmptyImage />
            <Text style={styles.noInsightsTitle}>{'There are no insights now'}</Text>
            <Text style={styles.noInsightsDescription}>
              {'Insights, anomalies and alerts will appear here'}
            </Text>
          </View>}
      </ScrollView>
      <ActivityIndicator
        size='large'
        style={Style.activityIndicator}
        animating={loading}
      />
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  scoreSection: {
    width: 132,
    marginStart: 24,
    borderRadius: 16,
    padding: 16,
    backgroundColor: Colors.light.hereworksWhite100,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    justifyContent: 'flex-end',
  },
  scoreValue: {
    fontSize: 24,
    lineHeight: 24,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
    marginBottom: 8,
  },
  scoreTitle: {
    fontSize: 12,
    lineHeight: 14.4,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  scoreSectionItemSeparator: {
    width: 64,
    height: 1.5,
    backgroundColor: Colors.light.hereworksWhite500,
    marginVertical: 16,
  },
  insightsSection: {
    flex: 1,
  },
  insightsTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    margin: 16,
  },
  emptyState: {
    padding: 24,
    justifyContent: 'center',
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksWhite500,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  noInsightsTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginTop: 32,
    textAlign: 'center',
  },
  noInsightsDescription: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    textAlign: 'center',
  },
  loadingOpacity: {
    opacity: 0.4,
  },
  list: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    marginBottom: -24,
    marginEnd: 8,
  },
});

export default TopSectionInsights;
